import React, {useState, useEffect} from 'react';
import {Box, Paper, Typography, Button, Link, Stack, Divider, Card, MobileStepper, Tooltip} from '@mui/material';
import { ChatMessage, useChat } from '../../../contexts/ChatContext';
import SmallLogo from "../../../components/logo/inbriefFullWhiteR_small.svg";
import {CHAT_FONT} from "@/constants/styles";
import {getDeterministicColor} from "@/utils/utils";
import Avatar from "@/components/@extended/Avatar";
import useAuth from "@/hooks/useAuth";
import ClipCard from "@/pages/custom-components/clipCard";
import {UI_ACTION_TYPE, UIFilterAction} from "@/types/chatTypes";
import ChevronRight from "@mui/icons-material/ChevronRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

interface MessageBubbleProps {
    message: ChatMessage;
    lastMessages: boolean;
}

const MAX_CHOICE_LABEL_LENGTH = 46;

// Le scelte "match" possono arrivare con nomi molto lunghi: tronchiamo per
// leggibilità e mostriamo il testo completo in un Tooltip al passaggio.
const formatChoiceLabel = (text: string): string => {
    if (text.length <= MAX_CHOICE_LABEL_LENGTH) return text;
    return `${text.slice(0, MAX_CHOICE_LABEL_LENGTH - 1).trimEnd()}…`;
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, lastMessages }) => {
    const isUser = message.type === 'user';
    const {user} = useAuth()
    const { activeChoices, sendChatMessage, executeDelayedMessagesActions } = useChat();

    // Selection and choices
    const isCurrentActiveChoices = message.type === 'agent' && message.choices && activeChoices === message.choices;
    const [selections, setSelections] = useState<string[]>([]);

    // collapse of text
    const [isExpanded, setIsExpanded] = useState(lastMessages ?? false)
    const MAX_LENGTH = 80;
    const shouldCollapse = (message.content.length > MAX_LENGTH) && !lastMessages ;

    // Media attributes
    const hasMedia : boolean = (message.media !== undefined && message.media !== null && message.media.length !== 0)

    // --- CAROSELLO ANTEPRIME MEDIA: una clip per slide, 'more' come slide finale ---
    const MAX_MEDIA_ITEMS = 10;
    const mediaItems = message.media ? message.media.slice(0, MAX_MEDIA_ITEMS) : [];
    type CarouselSlot = { clip?: (typeof mediaItems)[number]; isMore?: boolean };
    const carouselSlots: CarouselSlot[] = mediaItems.map((clip) => ({ clip }));
    if (mediaItems.length > 0) {
        carouselSlots.push({ isMore: true });
    }
    const totalSlides = carouselSlots.length;
    const [carouselIndex, setCarouselIndex] = useState(0);

    useEffect(() => {
        setCarouselIndex(0);
    }, [message.id]);

    const handleCarouselNext = () => setCarouselIndex((prev) => Math.min(prev + 1, totalSlides - 1));
    const handleCarouselBack = () => setCarouselIndex((prev) => Math.max(prev - 1, 0));

    useEffect(() => {
        setIsExpanded(lastMessages);
    }, [lastMessages]);

    const displayText = !isExpanded ?
        message.content.length >= MAX_LENGTH ?
        `${message.content.slice(0, MAX_LENGTH)}...` : `${message.content.slice(0, MAX_LENGTH)}`
        : message.content;

    const toggleExpand = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if(!lastMessages)
            setIsExpanded((prev) => !prev);
    };

    useEffect(() => {
        if (isCurrentActiveChoices && message.choices) {
            setSelections(new Array(message.choices.length).fill(''));
        }
    }, [isCurrentActiveChoices, message.choices]);

    const handleSelectOption = (groupIdx: number, option: string) => {
        if (!message.choices) return;

        const updatedSelections = [...selections];
        updatedSelections[groupIdx] = option;
        setSelections(updatedSelections);

        const allGroupsSelected = updatedSelections.every(selection => selection !== '');
        if (allGroupsSelected) {
            const finalMessage = updatedSelections.join(', ');
            sendChatMessage(finalMessage);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box', mb: 1 }}>
            {/* Header del Messaggio */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    px: 1
                }}
            >
                {/* Icon of the User who Created the message on the Header */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <Box sx={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        backgroundColor: isUser ? 'primary.light' : 'transparent',
                        color: isUser ? 'primary.contrastText' : 'secondary.contrastText',
                    }}>
                        {isUser ? (
                            <Avatar
                                sx={{
                                    bgcolor: getDeterministicColor(user?.name || '', user?.surname || ''),
                                    color: 'white',
                                    width: 30, height: 30, fontSize: '1rem', fontWeight: 'bold'
                                }}
                            >
                                {user && user.name && user.name[0]?.toUpperCase()}
                                {user && user.surname && user?.surname[0]?.toUpperCase()}
                            </Avatar>
                        ) : (
                            <img src={SmallLogo} width={'22px'} height={'22px'} alt="AI"/>
                        )}
                    </Box>
                </Box>

                {/* 2. Name of the creator of the message at the Header */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', opacity: 0.8, color: 'primary.contrastText', fontSize: CHAT_FONT }}>
                        {isUser ? user?.name : 'InBrief AI Copilot'}
                    </Typography>
                </Box>

                {/* 3. Time of Message at the end of the header */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.65rem', color: 'primary.contrastText' }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                </Box>
            </Box>
            <Divider sx={{my: 1, borderColor: 'primary.contrastText', opacity: 0.3}} />
            {/* ======================================================== */}
            <Box sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                width: '100%',
                pb: 1.5,
                boxSizing: 'border-box'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    gap: 1,
                    width: hasMedia ? '100%' : undefined,
                    maxWidth: hasMedia ? '100%' : '85%',
                    minWidth: 0
                }}>

                    {/* Bubble */}
                    <Paper sx={{
                        p: 1.5,
                        backgroundColor: 'transparent',
                        backgroundImage: 'none',
                        border: '2px solid transparent',
                        borderRadius: isUser ? '12px 12px 12px 12px' : '12px 12px 12px 12px',
                        borderColor: isUser ? 'primary.main' : 'secondary.main',
                        color: 'primary.contrastText',
                        width: hasMedia ? '100%' : undefined,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Typography sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', fontSize: CHAT_FONT, color: 'primary.contrastText' }}>
                            {displayText}
                        </Typography>
                        {shouldCollapse && (
                            <Link
                                component="button"
                                variant="body2"
                                onClick={(e) => toggleExpand(e)}
                                sx={{
                                    ml: 1,
                                    fontWeight: 'bold',
                                    fontSize: '0.8em',
                                    color: 'inherit',
                                    textDecoration: 'underline',
                                    opacity: 1,
                                    cursor: 'pointer',
                                    alignSelf: 'start',
                                    fontColor: 'primary.contrastText',
                                    '&:hover': { opacity: 1 }
                                }}
                            >
                                {isExpanded ? 'Show Less' : 'Show More'}
                            </Link>
                        )}

                        {/* Renderizzazione delle Scelte Interattive come LISTA DI PULSANTI */}
                        {isCurrentActiveChoices && message.choices && (
                            <Box sx={{ mt: 1.5 }}>
                                <Stack spacing={1.5} sx={{ width: '100%' }}>
                                    {message.choices.map((group, groupIdx) => (
                                        <Box key={groupIdx} sx={{ width: '100%' }}>
                                            {message.choices!.length > 1 && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: 'bold', display: 'block', color: 'secondary.contrastText', mb: 0.5, opacity: 0.8 }}
                                                >
                                                    Option {groupIdx + 1} di {message.choices!.length}:
                                                </Typography>
                                            )}

                                            {/* Opzioni come bottoni compatti: stessa riga finché c'è spazio, nessun radio button */}
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
                                                {group.map((option) => {
                                                    const isSelected = selections[groupIdx] === option;
                                                    const choiceLabel = formatChoiceLabel(option);
                                                    const isChoiceTruncated = choiceLabel !== option;
                                                    return (
                                                        <Tooltip
                                                            key={option}
                                                            title={isChoiceTruncated ? option : ''}
                                                            disableHoverListener={!isChoiceTruncated}
                                                            disableInteractive
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <Button
                                                                onClick={() => handleSelectOption(groupIdx, option)}
                                                                disableElevation
                                                                sx={{
                                                                    flex: '0 1 auto',
                                                                    maxWidth: '100%',
                                                                    minWidth: 56,
                                                                    justifyContent: 'center',
                                                                    textTransform: 'none',
                                                                    whiteSpace: 'normal',
                                                                    overflowWrap: 'anywhere',
                                                                    py: 0.75,
                                                                    px: 1.75,
                                                                    borderRadius: '8px',
                                                                    fontSize: CHAT_FONT,
                                                                    lineHeight: 1.3,
                                                                    border: '1px solid',
                                                                    borderColor: isSelected ? 'primary.main' : 'rgba(255,255,255,0.24)',
                                                                    bgcolor: isSelected ? 'primary.main' : 'rgba(255,255,255,0.08)',
                                                                    color: 'primary.contrastText',
                                                                    '&:hover': {
                                                                        bgcolor: isSelected ? 'primary.dark' : 'rgba(255,255,255,0.16)',
                                                                        borderColor: isSelected ? 'primary.dark' : 'rgba(255,255,255,0.32)',
                                                                    },
                                                                }}
                                                            >
                                                                {choiceLabel}
                                                            </Button>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        {/* Renderizzazione dei Media */}
                        {hasMedia && (() => {
                            let resourceType = 'clips';

                            const relevantAction = message?.actions?.find(a =>
                                a.type === UI_ACTION_TYPE.CREATE_CONTENT || a.type === UI_ACTION_TYPE.FILTER
                            );

                            if (relevantAction) {
                                if (relevantAction.type === UI_ACTION_TYPE.CREATE_CONTENT) {
                                    resourceType = 'clips';
                                } else if (relevantAction.type === UI_ACTION_TYPE.FILTER) {
                                    const params = relevantAction.params as UIFilterAction;
                                    resourceType = params?.resource_type?.toLowerCase().includes('clip') ? 'clips' : 'key_moments';
                                }
                            }

                            const moreLabel = resourceType.toLowerCase().includes('clip') ? 'More' : 'View in Library';
                            const activeSlot = carouselSlots[carouselIndex];

                            return (
                                <Box sx={{ mt: 0, width: '100%', overflow: 'hidden' }}>
                                    {/* HEADER */}
                                    <Box
                                        onClick={() => executeDelayedMessagesActions(message.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', cursor: 'pointer', width: '100%', py: 0.25,
                                            '&:hover .MuiSvgIcon-root': { color: 'primary.main' },
                                        }}
                                    >
                                        {(relevantAction && relevantAction.type === UI_ACTION_TYPE.FILTER) ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1, minWidth: 0 }}>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: `${CHAT_FONT} !important`,
                                                        fontWeight: '400 !important',
                                                        lineHeight: '1.4 !important',
                                                        color: 'primary.contrastText',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    View in Library
                                                </Typography>
                                                <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.3)' }} />
                                            </Box>
                                        ) : <Divider sx={{ flexGrow: 1, borderColor: 'rgba(255,255,255,0.3)' }} />}
                                        <ChevronRight sx={{ ml: 1, color: 'text.secondary' }} />
                                    </Box>

                                    {/* CAROSELLO: una clip a schermo intero per slide, senza scrollbar */}
                                    <Box sx={{ width: '100%' }}>
                                        {activeSlot && (
                                            <Box onClick={() => executeDelayedMessagesActions(message.id)} sx={{ width: '100%', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                                                <Box sx={{ width: 220, maxWidth: '78%' }}>
                                                    {activeSlot.isMore ? (
                                                        <Card
                                                            sx={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                aspectRatio: '16 / 9',
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mb:0.25,
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: '0.65rem', fontWeight: '400 !important', lineHeight: '1.4 !important', textAlign: 'center', px: 0.5 }}>
                                                                {moreLabel}
                                                            </Typography>
                                                        </Card>
                                                    ) : (
                                                        <ClipCard
                                                            card={activeSlot.clip}
                                                            resourceType={resourceType}
                                                            calcMinWidth={true}
                                                            smaller={true}
                                                            interactive={false}
                                                            toggleSelect={() => executeDelayedMessagesActions(message.id)}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        {totalSlides > 1 && (
                                            <MobileStepper
                                                variant="dots"
                                                steps={totalSlides}
                                                position="static"
                                                activeStep={carouselIndex}
                                                sx={{
                                                    bgcolor: 'transparent',
                                                    px: 0,
                                                    mt: 0,
                                                    minHeight: 0,
                                                    '& .MuiMobileStepper-dot': { bgcolor: 'rgba(255,255,255,0.3)', mx: 0.25, width: 5, height: 5 },
                                                    '& .MuiMobileStepper-dotActive': { bgcolor: 'primary.main' },
                                                }}
                                                nextButton={
                                                    <Button
                                                        size="small"
                                                        onClick={handleCarouselNext}
                                                        disabled={carouselIndex === totalSlides - 1}
                                                        endIcon={<KeyboardArrowRightIcon fontSize="small" />}
                                                        sx={{ minWidth: 0, marginRight: 0.25, py: 0, fontSize: '0.65rem', textTransform: 'none', color: 'primary.contrastText', '& .MuiButton-endIcon': { ml: 0.25 } }}
                                                    >
                                                        Next
                                                    </Button>
                                                }
                                                backButton={
                                                    <Button
                                                        size="small"
                                                        onClick={handleCarouselBack}
                                                        disabled={carouselIndex === 0}
                                                        startIcon={<KeyboardArrowLeftIcon fontSize="small" />}
                                                        sx={{ minWidth: 0, marginLeft: 0.25, py: 0, fontSize: '0.65rem', textTransform: 'none', color: 'primary.contrastText', '& .MuiButton-startIcon': { mr: 0.25 } }}
                                                    >
                                                        Prev
                                                    </Button>
                                                }
                                            />
                                        )}
                                    </Box>
                                </Box>
                            );
                        })()}
                        {(!hasMedia && message?.actions?.find(a =>
                            a.type === UI_ACTION_TYPE.CREATE_CONTENT || a.type === UI_ACTION_TYPE.FILTER
                            )) &&
                            (<Box
                                sx={{
                                    border: '3px solid',
                                    mx:'3px',
                                    borderColor: 'white',
                                    borderStyle: 'dashed',
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignSelf: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography>No data Available</Typography>
                            </Box>)
                        }
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};